import { DeepMap } from 'react-hook-form';
import { JobsiteUpdateInput } from 'apollo/generated/client-operations';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { JobsiteForms } from 'containers/jobsiteConfiguration/components';
import {
  sessionAssignmentOptions,
  autoCreateTriggerOptions,
} from 'containers/jobsiteConfiguration/components/jobsiteForms/modals';
import {
  EditJobsiteConfigurationFormData,
  FormOptionElement,
} from 'containers/jobsiteConfiguration/types';
import { ensureNonUndefinedFields, typedComponentProps } from 'utils';
import { Jobsite } from './types';

export type FormsSectionInputsArgs = {
  formsOptions: FormOptionElement[];
};

export const getFormsSectionInputs = (
  args: FormsSectionInputsArgs,
): TypedFormInputs<EditJobsiteConfigurationFormData['forms']> => {
  const { formsOptions } = args;
  return {
    jobsiteForms: {
      element: FormInputTypes.CustomInput,
      elementProps: {
        customInput: JobsiteForms,
        ...typedComponentProps<typeof JobsiteForms>({ formsOptions }),
      },
      layout: [GridColSpan.SpanFull],
    },
  };
};

export const getFormsSectionDefaultValues = (
  jobsite: Jobsite,
  formsOptions: FormOptionElement[],
): EditJobsiteConfigurationFormData['forms'] => {
  const jobsiteForms =
    jobsite?.jobsiteForms?.edges
      .map(
        ({
          node: jf,
        }): EditJobsiteConfigurationFormData['forms']['jobsiteForms'][number] => ({
          id: jf.id,
          changeType: null,
          form: formsOptions.find((opt) => opt.value === jf.form.id) ?? null,
          sessionAssignment:
            sessionAssignmentOptions.find(
              (opt) => opt.value === jf.sessionAssignment,
            ) ?? null,
          autoCreateTrigger:
            autoCreateTriggerOptions.find(
              (opt) => opt.value === jf.autoCreateTrigger,
            ) ?? null,
          alert: null,
          logDescription: null,
        }),
      )
      .sort((a, b) =>
        (a.form?.label ?? '').localeCompare(b.form?.label ?? ''),
      ) ?? [];

  return { jobsiteForms };
};

type FormsSectionUpdateInput = Required<
  Pick<JobsiteUpdateInput, 'jobsiteForms'>
>;

export const getFormsSectionUpdateInput = (
  forms: EditJobsiteConfigurationFormData['forms'],
  dirtyFields: DeepMap<EditJobsiteConfigurationFormData['forms'], true>,
): FormsSectionUpdateInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(forms, dirtyFields);

  const jobsiteForms = getUpdateInputValue('jobsiteForms')
    ?.filter((jf) => jf.changeType)
    .map((jf): FormsSectionUpdateInput['jobsiteForms'][number] => ({
      id: jf.id,
      formId: jf.form.value,
      sessionAssignment: jf.sessionAssignment && jf.sessionAssignment.value,
      autoCreateTrigger: jf.autoCreateTrigger && jf.autoCreateTrigger.value,
      changeType: jf.changeType,
    }));

  return ensureNonUndefinedFields<FormsSectionUpdateInput>({ jobsiteForms });
};
